import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import { CommonModule } from '@angular/common';
import { FormValueControl } from '@angular/forms/signals';
import { Day } from './day/day.component';

@Component({
  selector: 'lepi-pick-calendar',
  templateUrl: './pick-calendar.component.html',
  styleUrls: ['./pick-calendar.component.css'],
  imports: [CommonModule, Day],
})
export class PickCalendar
  implements OnInit, AfterViewInit, FormValueControl<Date | undefined>
{
  private readonly changeRef = inject(ChangeDetectorRef);

  mode = input<'single' | 'range'>('single');
  value = model<Date | undefined>(); // 단일 선택 모드 호환용
  startAt = model<Date | undefined>(); // 범위 선택 모드용
  endAt = model<Date | undefined>(); // 범위 선택 모드용

  // 달력이 현재 보여주는 월 (선택 날짜와 독립적으로 동작)
  private readonly today = new Date();
  viewDate = signal<Date>(this.today);
  years = signal<number[]>([]);
  year = computed(() => this.viewDate().getFullYear());

  // 초기화: value나 startAt이 있으면 해당 월로 이동
  constructor() {
    effect(() => {
      const year = this.year();
      if (year && year.toString()?.length === 4) {
        this.handleScroll(year);
      }
    });
  }

  ngOnInit(): void {
    // viewDate 초기값 설정
    const initial = this.value() || this.startAt() || this.today;
    this.viewDate.set(initial);

    const currentYear = new Date().getFullYear();
    const yearsArr: number[] = [];
    for (let i = currentYear - 20; i <= currentYear + 5; i++) {
      yearsArr.push(i);
    }
    this.years.set(yearsArr);
  }

  ngAfterViewInit(): void {
    this.handleScroll(this.year());
  }

  currentDate = computed(() => this.viewDate());

  days = computed(() => {
    const days: Dayjs[] = [];
    const current = dayjs(this.viewDate());

    const firstDayOfMonth = current.startOf('month');
    const lastDayOfMonth = current.endOf('month');
    const daysInMonth = current.daysInMonth();

    for (let i = firstDayOfMonth.day() - 1; i >= 0; i--) {
      days.push(firstDayOfMonth.subtract(i + 1, 'day'));
    }

    for (let i = 0; i < daysInMonth; i++) {
      days.push(firstDayOfMonth.add(i, 'day'));
    }

    for (let i = 1; i <= 6 - lastDayOfMonth.day(); i++) {
      days.push(lastDayOfMonth.add(i, 'day'));
    }

    return days;
  });

  handleScroll(year: number) {
    setTimeout(() => {
      const currentYear = document.getElementById(`${year}`);
      currentYear?.scrollIntoView({ block: 'center', behavior: 'auto' });
    }, 0);
  }

  handleNavigate(
    type: 'prev' | 'next',
    unit: 'year' | 'month',
    number: number
  ) {
    const baseDate = dayjs(this.viewDate());
    if (type === 'prev') {
      this.viewDate.set(baseDate.subtract(number, unit).toDate());
    } else {
      this.viewDate.set(baseDate.add(number, unit).toDate());
    }
  }

  handleSelectDay(day: Dayjs) {
    const selectedDate = day.toDate();

    if (this.mode() === 'single') {
      this.value.set(selectedDate);
      this.viewDate.set(selectedDate);
      return;
    }

    // 범위 선택 모드 (Skyscanner 스타일)
    if (!this.startAt() || (this.startAt() && this.endAt())) {
      // 시작일 선택 (또는 이미 둘 다 선택된 상태에서 새로 선택)
      this.startAt.set(selectedDate);
      this.endAt.set(undefined);
    } else {
      // 종료일 선택
      const start = dayjs(this.startAt());
      if (day.isBefore(start, 'day')) {
        this.startAt.set(selectedDate);
      } else {
        this.endAt.set(selectedDate);
      }
    }
  }

  handleSelectYear(year: number) {
    const val = dayjs(this.viewDate()).set('year', year).toDate();
    this.viewDate.set(val);
  }

  isStart(day: Dayjs) {
    return !!(this.startAt() && dayjs(this.startAt()).isSame(day, 'day'));
  }

  isEnd(day: Dayjs) {
    return !!(this.endAt() && dayjs(this.endAt()).isSame(day, 'day'));
  }

  isInRange(day: Dayjs) {
    if (!this.startAt() || !this.endAt()) return false;
    return (
      day.isAfter(dayjs(this.startAt()), 'day') &&
      day.isBefore(dayjs(this.endAt()), 'day')
    );
  }
}
